import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'

export default function ClientSetupPage() {
  const navigate = useNavigate()
  const [step, setStep] = useState('loading') // loading, setup, done, error
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [clientData, setClientData] = useState(null)
  const [session, setSession] = useState(null)

  useEffect(() => {
    async function handleInvite() {
      // Get session from URL hash (Supabase puts it there after invite click)
      const { data: { session }, error } = await supabase.auth.getSession()

      if (error || !session) {
        // Try to exchange the token from URL
        const hash = window.location.hash
        if (hash) {
          const params = new URLSearchParams(hash.substring(1))
          const accessToken = params.get('access_token')
          const refreshToken = params.get('refresh_token')

          if (accessToken) {
            const { data, error: sessionError } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken
            })
            if (sessionError) { setStep('error'); return }
            setSession(data.session)
            await loadClientData(data.session.user)
            return
          }
        }
        setStep('error')
        return
      }

      setSession(session)
      await loadClientData(session.user)
    }

    handleInvite()
  }, [])

  async function loadClientData(user) {
    // Find client record by email
    const { data: client } = await supabase
      .from('clients')
      .select('*')
      .eq('email', user.email)
      .maybeSingle()

    if (client) {
      setClientData(client)
      // If client already has profile linked, go to dashboard
      if (client.profile_id) {
        navigate('/dashboard')
        return
      }
    }
    setStep('setup')
  }

  async function handleSetup(e) {
    e.preventDefault()
    if (password !== confirmPassword) { setError('Passwords do not match'); return }
    if (password.length < 8) { setError('Password must be at least 8 characters'); return }
    setLoading(true)
    setError('')

    try {
      // Update password
      const { error: pwError } = await supabase.auth.updateUser({ password })
      if (pwError) { setError(pwError.message); setLoading(false); return }

      // Get current user + session
      const { data: { session } } = await supabase.auth.getSession()
      const user = session?.user

      // Use serverless function to create profile and link client (bypasses RLS)
      const linkRes = await fetch('/.netlify/functions/link-client', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`
        },
        body: JSON.stringify({
          user_id: user.id,
          email: user.email,
          full_name: clientData?.full_name || user.email
        })
      })
      const linkData = await linkRes.json()
      if (linkData.error) { setError(linkData.error); setLoading(false); return }

      setStep('done')
      setTimeout(() => navigate('/dashboard'), 2000)
    } catch (err) {
      setError(err.message)
    }
    setLoading(false)
  }

  const containerStyle = {
    minHeight: '100vh',
    background: '#202326',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    fontFamily: "'DM Sans', sans-serif"
  }

  const cardStyle = {
    background: '#fff',
    borderRadius: 16,
    padding: '40px 36px',
    width: '100%',
    maxWidth: 420,
    boxShadow: '0 24px 64px rgba(0,0,0,0.3)'
  }

  const mobileStyleBlock = (
    <style>{`
      @media (max-width: 768px) {
        .client-setup-root {
          padding: 14px !important;
        }
        .client-setup-card {
          padding: 28px 20px !important;
          border-radius: 12px !important;
        }
      }
    `}</style>
  )

  if (step === 'loading') return (
    <div className="client-setup-root" style={containerStyle}>
      {mobileStyleBlock}
      <div style={{ textAlign: 'center', color: '#fff' }}>
        <div style={{ fontSize: 40, marginBottom: 16 }}>⏳</div>
        <div style={{ fontSize: 16 }}>Setting up your portal…</div>
      </div>
    </div>
  )

  if (step === 'error') return (
    <div className="client-setup-root" style={containerStyle}>
      {mobileStyleBlock}
      <div className="client-setup-card" style={{ ...cardStyle, textAlign: 'center' }}>
        <div style={{ fontSize: 40, marginBottom: 16 }}>❌</div>
        <h2 style={{ marginBottom: 8, color: '#202326' }}>Invalid Invite Link</h2>
        <p style={{ color: '#62615D', fontSize: 14, marginBottom: 20 }}>This invite link has expired or is invalid. Please contact your accountant for a new invite.</p>
        <button onClick={() => navigate('/login')} style={{ background: '#202326', color: '#CBB17B', border: 'none', borderRadius: 8, padding: '12px 24px', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>
          Go to Login
        </button>
      </div>
    </div>
  )

  if (step === 'done') return (
    <div className="client-setup-root" style={containerStyle}>
      {mobileStyleBlock}
      <div className="client-setup-card" style={{ ...cardStyle, textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🎉</div>
        <h2 style={{ marginBottom: 8, color: '#202326' }}>You're all set!</h2>
        <p style={{ color: '#62615D', fontSize: 14 }}>Your ClearFile portal is ready. Taking you to your dashboard…</p>
        <div style={{ marginTop: 20, height: 4, background: '#F0EBE0', borderRadius: 2, overflow: 'hidden' }}>
          <div style={{ height: '100%', background: '#CBB17B', borderRadius: 2, animation: 'progressBar 2s linear forwards' }} />
        </div>
      </div>
    </div>
  )

  return (
    <div className="client-setup-root" style={containerStyle}>
      {mobileStyleBlock}
      <div className="client-setup-card" style={cardStyle}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 28, fontWeight: 700, color: '#CBB17B', letterSpacing: '-0.5px' }}>ClearFile</div>
          <div style={{ fontSize: 12, color: '#62615D', marginTop: 2 }}>Your Client Portal</div>
        </div>

        <h2 style={{ fontSize: 20, color: '#202326', marginBottom: 4 }}>Welcome aboard!</h2>
        <p style={{ color: '#62615D', fontSize: 13, marginBottom: 24, lineHeight: 1.6 }}>
          {clientData
            ? `Hi ${clientData.full_name}! Create a password to access your portal.`
            : 'Create a password to access your ClearFile portal.'}
        </p>

        <form onSubmit={handleSetup}>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#202326', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Password
            </label>
            <input
              type="password"
              placeholder="Minimum 8 characters"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              style={{ width: '100%', padding: '12px 14px', borderRadius: 8, border: '1.5px solid #DED6CA', fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: "'DM Sans', sans-serif" }}
            />
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#202326', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Confirm Password
            </label>
            <input
              type="password"
              placeholder="Re-enter your password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              required
              style={{ width: '100%', padding: '12px 14px', borderRadius: 8, border: '1.5px solid #DED6CA', fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: "'DM Sans', sans-serif" }}
            />
          </div>

          {error && (
            <div style={{ background: '#FEE2E2', color: '#991B1B', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 14 }}>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%', padding: '13px', background: loading ? '#D4C9B5' : '#202326',
              color: '#CBB17B', border: 'none', borderRadius: 8, fontSize: 15,
              fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer',
              fontFamily: "'DM Sans', sans-serif", transition: 'background 0.2s'
            }}
          >
            {loading ? 'Setting up your account…' : 'Activate My Portal →'}
          </button>
        </form>

        <p style={{ textAlign: 'center', fontSize: 12, color: '#62615D', marginTop: 20 }}>
          Already have an account? <a href="/login" style={{ color: '#CBB17B', fontWeight: 600 }}>Sign in</a>
        </p>
      </div>
    </div>
  )
}
